import { useCallback, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  setWinAnimationsPlaying,
  setResetMainGameVisualEffects,
  setCounterAnimationStarted,
  setCounterAnimationFinished,
  setCriticalAnimationPlaying,
  setExpandingWildsOccurred,
  setSkipWinAnimation,
  setUserWantsToDismissWinAnimation,
  setSkipCounterAnimation,
  setIsWaitingForUserConfirmation,
  closeAllPopups,
  setSymbolPopupBlocked,
  openTurboSuggestionPopup,
  updateDisplayedBalance,
  setTurboSuggestionPending,
  setShowHoldAndWinIntro,
  openHoldAndWinResumePopup,
  setShowHoldAndWinCollector,
} from "../State/actions";
import {
  selectResetMainGameVisualEffects,
  selectCurrentRound,
  selectWinningLines,
  selectBet,
  selectSpinComplete,
  selectIsSpinning,
  selectTurboSuggestionPending,
  selectFreeSpinsActive,
  selectFlowState,
  selectLastCommand,
  selectRespinsLeft,
  selectHoldAndWinSpinComplete,
  selectHoldAndWinIsSpinning,
  selectCriticalAnimationPlaying,
  selectHoldAndWinActive,
  selectAutoPlayActive,
  selectFeatures,
  selectIntroPopupActive,
  selectIsHoldAndWinIntroReload,
  selectIsHoldAndWinMidFeatureReload,
} from "../State/selectors";
import { SOUND_NAME } from "../sounds";
import { useAppActivity } from "../Contexts/AppActivityContext";
import { useReelRefs } from "../Contexts/ReelRefsContext";
import { useReelControl } from "../Contexts/ReelControlContext";
import { GAME_LAYOUT } from "../settings";
import { SCATTER_SYMBOL_ID, COIN_SYMBOL_IDS } from "../SymbolData";
import {
  FEATURES_CONFIG,
  WIN_TYPES,
  detectExpandingWilds,
  playCascadingExpandingWild,
  playFullReelExpandingWild,
  getAppearAnimationDuration,
} from "../featuresConfig";
import { soundManager } from "../SoundManager";
import { getWinType } from "../utilities";

// Hook for managing win line animations, sound effects, and reel coordination
export const useWinController = () => {
  const {
    reelRefs,
    overlayReelRefs,
    winLinesContainerRef,
    dimReelSetRef,
    globalLineWinTextRef,
    lineWinTextReelRefs,
  } = useReelRefs();

  const { spacebarTurboActive } = useReelControl();

  const dispatch = useDispatch();

  const spinComplete = useSelector(selectSpinComplete);
  const isSpinning = useSelector(selectIsSpinning);
  const currentRound = useSelector(selectCurrentRound);
  const winningLines = useSelector(selectWinningLines);
  const bet = useSelector(selectBet);
  const turboSuggestionPending = useSelector(selectTurboSuggestionPending);
  const freeSpinsActive = useSelector(selectFreeSpinsActive);
  const resetMainGameVisualEffects = useSelector(
    selectResetMainGameVisualEffects
  );
  const flowState = useSelector(selectFlowState);
  const lastCommand = useSelector(selectLastCommand);
  const respinsLeft = useSelector(selectRespinsLeft);
  const holdAndWinSpinComplete = useSelector(selectHoldAndWinSpinComplete);
  const holdAndWinIsSpinning = useSelector(selectHoldAndWinIsSpinning);
  const criticalAnimationPlaying = useSelector(selectCriticalAnimationPlaying);
  const holdAndWinActive = useSelector(selectHoldAndWinActive);
  const autoPlayActive = useSelector(selectAutoPlayActive);
  const features = useSelector(selectFeatures);
  const introPopupActive = useSelector(selectIntroPopupActive);
  const isHoldAndWinIntroReload = useSelector(selectIsHoldAndWinIntroReload);
  const isHoldAndWinMidFeatureReload = useSelector(selectIsHoldAndWinMidFeatureReload);

  // Refs for state management and animation control
  const reelsetControllerRef = useRef(null);
  const currentTimeoutsRef = useRef([]);
  const pendingAnimationFramesRef = useRef([]);
  const isAnimatingRef = useRef(false);
  const isReadyRef = useRef(false);
  const animationCompletedRef = useRef(false);
  const isFirstAnimationRef = useRef(true);
  const spinCompleteRef = useRef(spinComplete);
  const currentRoundRef = useRef(currentRound);
  const winningLinesRef = useRef(winningLines);
  const winningDataRef = useRef(null);
  const lineIdsRef = useRef([]);
  const currentLineIndexRef = useRef(0);
  const dispatchRef = useRef(dispatch);
  const { isActive } = useAppActivity();
  const activityRef = useRef(true);
  const turboActiveRef = useRef(false);
  const winSoundPlayedRef = useRef(false);
  const expandingWildReelIndicesRef = useRef([]);
  const animatedRoundsRef = useRef(new Set());
  const holdAndWinOutroTriggeredRef = useRef(false);
  const executeOnReelControllersRef = useRef(null);
  const executeOnOverlayReelControllersRef = useRef(null);

  /**
   * Detects if current round is a hold and win trigger round.
   * Returns true when lastCommand was "spin" and flowState is "respin".
   */
  const detectHoldAndWin = useCallback(() => {
    return lastCommand === "spin" && flowState === "respin";
  }, [lastCommand, flowState]);

  /**
   * Detects if hold and win feature is ending (ready to show outro).
   * Returns true when all respins are complete and feature is closing.
   */
  const detectHoldAndWinOutro = useCallback(() => {
    return (
      holdAndWinActive === true &&
      lastCommand === "respin" &&
      flowState === "closed" &&
      respinsLeft === 0 &&
      holdAndWinSpinComplete === true &&
      holdAndWinIsSpinning === false
    );
  }, [
    holdAndWinActive,
    lastCommand,
    flowState,
    respinsLeft,
    holdAndWinSpinComplete,
    holdAndWinIsSpinning,
  ]);

  /**
   * Clears all active timeouts and pending animation frames.
   * Prevents memory leaks and race conditions when animations are interrupted or component unmounts.
   */
  const clearAllTimeouts = useCallback(() => {
    const timeouts = currentTimeoutsRef.current;
    for (let i = 0; i < timeouts.length; i++) {
      clearTimeout(timeouts[i]);
    }
    currentTimeoutsRef.current = [];

    const animationFrames = pendingAnimationFramesRef.current;
    for (let i = 0; i < animationFrames.length; i++) {
      cancelAnimationFrame(animationFrames[i]);
    }
    pendingAnimationFramesRef.current = [];
  }, []);

  /**
   * Shows coin win animations for Hold and Win trigger.
   * Collects coin positions, plays win animations, then waits for duration.
   * Animations remain visible when Hold and Win intro shows.
   */
  const showCoinAnimationsForHoldAndWin = useCallback(async () => {
    // Collect coin positions from currentRound.reels
    const coinPositions = [];
    const expandedReels = currentRoundRef.current?.reels;

    if (expandedReels && Array.isArray(expandedReels)) {
      for (let reelIndex = 0; reelIndex < expandedReels.length; reelIndex++) {
        const reel = expandedReels[reelIndex];
        if (!Array.isArray(reel)) continue;

        for (let symbolIndex = 0; symbolIndex < reel.length; symbolIndex++) {
          const symbolId = reel[symbolIndex];

          // Check if this is a coin symbol
          if (symbolId && COIN_SYMBOL_IDS.includes(symbolId)) {
            coinPositions.push([reelIndex, symbolIndex, symbolId]);
          }
        }
      }
    }

    if (coinPositions.length === 0) {
      return;
    }

    executeOnOverlayReelControllersRef.current((overlayReelController) => {
      overlayReelController?.hideAllAnimations();
    });

    executeOnReelControllersRef.current((controller) => {
      if (controller && controller.showAllSymbolTextures) {
        controller.showAllSymbolTextures();
      }
    });

    if (dimReelSetRef.current) {
      dimReelSetRef.current.dimReels();
    }
    for (let i = 0; i < coinPositions.length; i++) {
      const [reelIndex, symbolIndex] = coinPositions[i];
      const overlayReelController =
        reelsetControllerRef.current?.overlayReelControllers?.[reelIndex];
      const reelController =
        reelsetControllerRef.current?.reelControllers?.[reelIndex];

      if (!overlayReelController || !reelController) continue;

      const symbol = reelController.symbolsContainer.children[symbolIndex];

      if (symbol) {
        const actualSymbolId = symbol.currentSymbolId || symbol.symbolId;
        if (actualSymbolId !== undefined) {
          overlayReelController.showWinAnimation(symbolIndex, actualSymbolId);
        }
      }
    }

    // Play coin_win sound when showing coin win animations (feature trigger)
    soundManager.play(SOUND_NAME.coin_win);

    const rafId = requestAnimationFrame(() => {
      const index = pendingAnimationFramesRef.current.indexOf(rafId);
      if (index > -1) {
        pendingAnimationFramesRef.current.splice(index, 1);
      }

      for (let i = 0; i < coinPositions.length; i++) {
        const [reelIndex, symbolIndex] = coinPositions[i];
        const reelController =
          reelsetControllerRef.current?.reelControllers?.[reelIndex];

        if (reelController && reelController.hideSymbolTexture) {
          reelController.hideSymbolTexture(symbolIndex);
        }
      }
    });
    pendingAnimationFramesRef.current.push(rafId);

    const rafId2 = requestAnimationFrame(() => {
      const index = pendingAnimationFramesRef.current.indexOf(rafId2);
      if (index > -1) {
        pendingAnimationFramesRef.current.splice(index, 1);
      }

      for (let i = 0; i < coinPositions.length; i++) {
        const [reelIndex, symbolIndex] = coinPositions[i];
        const reelController =
          reelsetControllerRef.current?.reelControllers?.[reelIndex];

        if (reelController && reelController.hideSymbolTexture) {
          reelController.hideSymbolTexture(symbolIndex);
        }
      }
    });
    pendingAnimationFramesRef.current.push(rafId2);

    const displayDuration =
      FEATURES_CONFIG.WIN_ANIMATION_TIMING.LINE_CYCLE_DURATION;

    await new Promise((resolve) => {
      const timeoutId = setTimeout(() => {
        const index = currentTimeoutsRef.current.indexOf(timeoutId);
        if (index > -1) {
          currentTimeoutsRef.current.splice(index, 1);
        }
        resolve();
      }, displayDuration);
      currentTimeoutsRef.current.push(timeoutId);
    });
  }, [dimReelSetRef]);

  /**
   * Effect: Handle Hold and Win reload scenarios
   * - Intro reload: Wait for IntroPopup to close, then show natural HoldAndWinIntroDialog
   * - Mid-feature reload: Wait for IntroPopup to close, then show reload dialog
   * Uses reload flags from Redux to prevent triggering during normal gameplay
   */
  useEffect(() => {
    if (!spinComplete || !features) {
      return;
    }

    // Intro reload: Wait for IntroPopup to close, then show natural Hold and Win intro
    if (
      FEATURES_CONFIG.HOLD_AND_WIN.ENABLED &&
      isHoldAndWinIntroReload &&
      !introPopupActive
    ) {
      console.log("🟣 [useWinController] Handling intro reload, showing natural Hold and Win intro");
      // Show natural Hold and Win intro dialog (not reload dialog)
      dispatch(setShowHoldAndWinIntro(true));
      return;
    }

    // Mid-feature reload: Wait for IntroPopup to close, then show reload dialog
    if (
      FEATURES_CONFIG.HOLD_AND_WIN.ENABLED &&
      isHoldAndWinMidFeatureReload &&
      !introPopupActive
    ) {
      console.log("🟣 [useWinController] Handling mid-feature reload, showing reload dialog");
      // Show Hold and Win reload dialog
      dispatch(openHoldAndWinResumePopup());
      return;
    }
  }, [spinComplete, isHoldAndWinIntroReload, isHoldAndWinMidFeatureReload, introPopupActive, features, dispatch]);

  /**
   * Main effect: Orchestrates win animations after spin completes.
   * Handles expanding wilds (plays wild animation first) and normal wins.
   * Triggers BIG_WIN+ animations for large wins and prevents duplicate animations.
   */
  useEffect(() => {
    if (
      !isReadyRef.current ||
      !reelsetControllerRef.current ||
      !spinComplete ||
      isAnimatingRef.current ||
      animationCompletedRef.current
    ) {
      return;
    }

    if (FEATURES_CONFIG.HOLD_AND_WIN.ENABLED && !holdAndWinActive) {
      const isHoldAndWinTrigger = detectHoldAndWin();

      if (isHoldAndWinTrigger) {
        animationCompletedRef.current = false;
        isFirstAnimationRef.current = true;
        animatedRoundsRef.current.clear();
        winSoundPlayedRef.current = false;
        winningDataRef.current = null;
        lineIdsRef.current = [];
        currentLineIndexRef.current = 0;
        holdAndWinOutroTriggeredRef.current = false;

        dispatch(setWinAnimationsPlaying(false));
        dispatch(setCounterAnimationStarted(false));
        dispatch(setCounterAnimationFinished(false));
        dispatch(setCriticalAnimationPlaying(true));

        // Check for scatter wins (expanding wilds don't occur with scatter)
        const hasScatterWin =
          currentRound.wins &&
          Array.isArray(currentRound.wins) &&
          currentRound.wins.some(
            (win) => win.id === "scatter" || win.symbol === SCATTER_SYMBOL_ID
          );

        // Detect expanding wilds (same logic as normal win flow)
        const expandingWildReels =
          FEATURES_CONFIG.EXPANDING_WILDS.ENABLED && !hasScatterWin
            ? detectExpandingWilds(currentRoundRef, currentRound.wins)
            : [];
        const hasExpandingWilds = expandingWildReels.length > 0;

        const expandingWildReelIndices = expandingWildReels.map(
          ({ reelIndex }) => reelIndex
        );
        expandingWildReelIndicesRef.current = expandingWildReelIndices;

        // Check if there are regular line wins alongside coins
        const hasRegularWins = currentRound?.win && currentRound.win > 0 && winningLines;

        const holdAndWinDelayId = setTimeout(() => {
          (async () => {
            // If there are expanding wilds, play them FIRST (unskippable)
            if (hasExpandingWilds) {
              dispatch(setExpandingWildsOccurred(true));
              dispatch(setCriticalAnimationPlaying(true));

              // All expanding wilds play the same sound
              soundManager.play(SOUND_NAME.expanding_wild);

              const animationMode = FEATURES_CONFIG.EXPANDING_WILDS.ANIMATION_MODE;

              let animationPromises = [];

              if (animationMode === "full_reel") {
                animationPromises = expandingWildReels.map(({ reelIndex }) =>
                  playFullReelExpandingWild(
                    reelIndex,
                    reelRefs,
                    overlayReelRefs,
                    currentTimeoutsRef
                  )
                );
              } else if (animationMode === "cascade") {
                animationPromises = expandingWildReels.map(
                  ({ reelIndex, wildPosition }) =>
                    playCascadingExpandingWild(
                      reelIndex,
                      wildPosition,
                      reelRefs,
                      overlayReelRefs,
                      currentTimeoutsRef
                    )
                );
              }

              if (animationPromises.length > 0) {
                await Promise.all(animationPromises);
              }

              // Calculate appear animation duration
              let maxAppearAnimationDuration = 0;
              expandingWildReels.forEach(({ reelIndex }) => {
                const overlayReelRef = overlayReelRefs.current?.[reelIndex];
                const overlayReelController =
                  overlayReelRef?.getOverlayReelController?.();
                if (overlayReelController) {
                  const appearAnimationDuration = getAppearAnimationDuration(
                    overlayReelController
                  );
                  maxAppearAnimationDuration = Math.max(
                    maxAppearAnimationDuration,
                    appearAnimationDuration
                  );
                }
              });

              if (maxAppearAnimationDuration === 0) {
                maxAppearAnimationDuration = 1000;
              }

              // Wait for expanding wild animation to complete
              await new Promise((resolve) => {
                const timeoutId = setTimeout(() => {
                  const index = currentTimeoutsRef.current.indexOf(timeoutId);
                  if (index > -1) {
                    currentTimeoutsRef.current.splice(index, 1);
                  }

                  // Hide cascade animations if needed
                  if (animationMode === "cascade") {
                    executeOnOverlayReelControllersRef.current(
                      (overlayReelController) => {
                        overlayReelController?.hideAllAnimations();
                      }
                    );
                  }

                  dispatch(setCriticalAnimationPlaying(false));
                  resolve();
                }, maxAppearAnimationDuration);
                currentTimeoutsRef.current.push(timeoutId);
              });
            }

            // If there are regular wins, show them (all wins at once, unskippable)
            if (hasRegularWins) {
              const winningData = reelsetControllerRef.current.matchHorizontalWinningLines(
                winningLines,
                currentRound.wins
              );

              if (winningData && Object.keys(winningData).length > 0) {
                // Show counter animation for the total win
                dispatch(setCounterAnimationStarted(true));
                dispatch(updateDisplayedBalance());

                // Show all winning symbols at once (no line cycling, unskippable)
                await showAllWinningSymbolsForHoldAndWinTriggerRef.current(winningData);
              }
            }

            // Then show coin animations
            await showCoinAnimationsForHoldAndWin();

            console.log("🟣 [useWinController] Natural trigger, showing Hold and Win intro dialog");
            // Finally show Hold and Win intro
            dispatch(setShowHoldAndWinIntro(true));
          })();
        }, FEATURES_CONFIG.WIN_ANIMATION_TIMING.WIN_LINES_DELAY);

        currentTimeoutsRef.current.push(holdAndWinDelayId);
        return;
      }
    }

    if (FEATURES_CONFIG.HOLD_AND_WIN.ENABLED) {
      const isHoldAndWinOutro = detectHoldAndWinOutro();

      if (isHoldAndWinOutro && !criticalAnimationPlaying) {
        dispatch(setCriticalAnimationPlaying(true));
      }

      if (isHoldAndWinOutro && !holdAndWinOutroTriggeredRef.current) {
        holdAndWinOutroTriggeredRef.current = true;

        const holdAndWinCollectorTimeoutId = setTimeout(() => {
          dispatch(setShowHoldAndWinCollector(true));
        }, 1000);

        currentTimeoutsRef.current.push(holdAndWinCollectorTimeoutId);
        return;
      }
    }

    if (FEATURES_CONFIG.HOLD_AND_WIN.ENABLED && holdAndWinActive) {
      return;
    }

    const hasWins = currentRound?.win && currentRound.win > 0;
    if (!hasWins || !winningLines) {
      return;
    }

    const winType = getWinType(currentRound.win, bet, currentRound?.type);
    const isAnimatedWin =
      winType &&
      [
        WIN_TYPES.BIG_WIN,
        WIN_TYPES.MEGA_WIN,
        WIN_TYPES.SUPER_MEGA_WIN,
      ].includes(winType);

    const hasScatterWin =
      currentRound.wins &&
      Array.isArray(currentRound.wins) &&
      currentRound.wins.some(
        (win) => win.id === "scatter" || win.symbol === SCATTER_SYMBOL_ID
      );

    const expandingWildReels =
      FEATURES_CONFIG.EXPANDING_WILDS.ENABLED && !hasScatterWin
        ? detectExpandingWilds(currentRoundRef, currentRound.wins)
        : [];
    const hasExpandingWilds = expandingWildReels.length > 0;

    const expandingWildReelIndices = expandingWildReels.map(
      ({ reelIndex }) => reelIndex
    );
    expandingWildReelIndicesRef.current = expandingWildReelIndices;

    const wins = currentRound?.wins;
    const winsHash =
      Array.isArray(wins) && wins.length > 0 ? JSON.stringify(wins) : null;
    const roundId = currentRound?.id || "unknown";
    const uniqueAnimationId = `${roundId}-${winsHash}`;

    if (animatedRoundsRef.current.has(uniqueAnimationId)) {
      return;
    }

    animatedRoundsRef.current.add(uniqueAnimationId);

    const delayId = setTimeout(() => {
      if (hasExpandingWilds) {
        dispatch(setExpandingWildsOccurred(true));
        dispatch(setCriticalAnimationPlaying(true));

        // All expanding wilds play the same sound
        soundManager.play(SOUND_NAME.expanding_wild);

        const animationMode = FEATURES_CONFIG.EXPANDING_WILDS.ANIMATION_MODE;

        (async () => {
          let animationPromises = [];

          if (animationMode === "full_reel") {
            animationPromises = expandingWildReels.map(({ reelIndex }) =>
              playFullReelExpandingWild(
                reelIndex,
                reelRefs,
                overlayReelRefs,
                currentTimeoutsRef
              )
            );
          } else if (animationMode === "cascade") {
            animationPromises = expandingWildReels.map(
              ({ reelIndex, wildPosition }) =>
                playCascadingExpandingWild(
                  reelIndex,
                  wildPosition,
                  reelRefs,
                  overlayReelRefs,
                  currentTimeoutsRef
                )
            );
          }

          if (animationPromises.length > 0) {
            await Promise.all(animationPromises);
          }

          let maxAppearAnimationDuration = 0;

          // Calculate appear animation duration for both cascade and full_reel modes
          expandingWildReels.forEach(({ reelIndex }) => {
            const overlayReelRef = overlayReelRefs.current?.[reelIndex];
            const overlayReelController =
              overlayReelRef?.getOverlayReelController?.();
            if (overlayReelController) {
              const appearAnimationDuration = getAppearAnimationDuration(
                overlayReelController
              );
              maxAppearAnimationDuration = Math.max(
                maxAppearAnimationDuration,
                appearAnimationDuration
              );
            }
          });

          // Fallback if duration couldn't be determined
          if (maxAppearAnimationDuration === 0) {
            maxAppearAnimationDuration = 1000;
          }

          const wildAnimationCompleteTimeoutId = setTimeout(async () => {
            if (animationMode === "cascade") {
              executeOnOverlayReelControllersRef.current(
                (overlayReelController) => {
                  overlayReelController?.hideAllAnimations();
                }
              );
            }

            dispatch(setCriticalAnimationPlaying(false));

            const winningData =
              reelsetControllerRef.current.matchHorizontalWinningLines(
                winningLines,
                currentRound.wins
              );

            if (winningData && Object.keys(winningData).length > 0) {
              dispatch(setCounterAnimationStarted(true));
              dispatch(updateDisplayedBalance());

              if (
                FEATURES_CONFIG.EXPANDING_WILDS.SHOW_ALL_WINS_BEFORE_CYCLING
              ) {
                if (winType && !winSoundPlayedRef.current) {
                  if (isAnimatedWin) {
                    // Don't play sound here - WinAnimationController handles animated win sounds
                    winSoundPlayedRef.current = true;
                  }
                }

                if (isAnimatedWin && currentRound?.type !== "feature") {
                  const bigWinDelay =
                    FEATURES_CONFIG.WIN_ANIMATION_TIMING
                      .BIG_WIN_DELAY_WITH_SHOW_ALL;

                  const bigWinTimeoutId = setTimeout(() => {
                    dispatch(closeAllPopups());
                    dispatch(setSymbolPopupBlocked(true));
                    dispatch(setWinAnimationsPlaying(true));
                  }, bigWinDelay);

                  currentTimeoutsRef.current.push(bigWinTimeoutId);
                }

            const shouldProceed = await showAllWinningSymbolsOnceRef.current(
              winningData,
              true,
              expandingWildReelIndices
            );

            // Check if we should proceed with line cycling
            // showAllWinningSymbolsOnce returns false if turbo was active during display
            if (shouldProceed && !turboActiveRef.current) {
              startWinningLinesAnimationRef.current(winningData);
            } else {
              // Clean up "show all wins" state before skipping to next spin
              if (FEATURES_CONFIG.WIN_LINES.ENABLED && winLinesContainerRef.current) {
                winLinesContainerRef.current.hideWinLine();
              }
              if (globalLineWinTextRef?.current) {
                globalLineWinTextRef.current.hide();
              }
              // Also hide per-line win texts to prevent residual visibility
              const middleReelLineWinText =
                lineWinTextReelRefs?.current?.[GAME_LAYOUT.MIDDLE_REEL];
              const secondReelLineWinText =
                lineWinTextReelRefs?.current?.[GAME_LAYOUT.SECOND_REEL];
              middleReelLineWinText?.hideAllLineWinTexts();
              secondReelLineWinText?.hideAllLineWinTexts();
            }
          }

              if (
                !FEATURES_CONFIG.EXPANDING_WILDS.SHOW_ALL_WINS_BEFORE_CYCLING
              ) {
                if (isAnimatedWin && currentRound?.type !== "feature") {
                  const bigWinDelay =
                    FEATURES_CONFIG.WIN_ANIMATION_TIMING
                      .BIG_WIN_DELAY_WITHOUT_SHOW_ALL;

                  const bigWinTimeoutId = setTimeout(() => {
                    dispatch(closeAllPopups());
                    dispatch(setSymbolPopupBlocked(true));
                    dispatch(setWinAnimationsPlaying(true));
                  }, bigWinDelay);

                  currentTimeoutsRef.current.push(bigWinTimeoutId);
                }
              }
            }
          }, maxAppearAnimationDuration);

          currentTimeoutsRef.current.push(wildAnimationCompleteTimeoutId);
        })();
      } else {
        const winningData =
          reelsetControllerRef.current.matchHorizontalWinningLines(
            winningLines,
            currentRound.wins
          );

        if (!winningData || Object.keys(winningData).length === 0) {
          return;
        }

        dispatch(setCriticalAnimationPlaying(true));

        const shouldShowAllWins = FEATURES_CONFIG.SHOW_ALL_WINS.ENABLED;
        const isScatterWin = currentRound?.type === "feature";
        const skipShowAllWins =
          isScatterWin && FEATURES_CONFIG.SHOW_ALL_WINS.EXCLUDE_SCATTER;

        (async () => {
          dispatch(setCriticalAnimationPlaying(false));
          dispatch(setCounterAnimationStarted(true));
          dispatch(updateDisplayedBalance());

          let shouldProceed = true; // Default to true if we skip showAllWins

          if (shouldShowAllWins && !skipShowAllWins) {
            if (winType && !winSoundPlayedRef.current) {
              if (isAnimatedWin) {
                // Don't play sound here - WinAnimationController handles animated win sounds
                winSoundPlayedRef.current = true;
              }
            }

            if (isAnimatedWin && currentRound?.type !== "feature") {
              const bigWinDelay =
                FEATURES_CONFIG.WIN_ANIMATION_TIMING
                  .BIG_WIN_DELAY_WITH_SHOW_ALL;

              const bigWinTimeoutId = setTimeout(() => {
                dispatch(closeAllPopups());
                dispatch(setWinAnimationsPlaying(true));
              }, bigWinDelay);

              currentTimeoutsRef.current.push(bigWinTimeoutId);
            }

            shouldProceed = await showAllWinningSymbolsOnceRef.current(winningData, false, []);
          }

          // Check if we should proceed with line cycling
          // showAllWinningSymbolsOnce returns false if turbo was active during display
          if (shouldProceed && !turboActiveRef.current) {
            startWinningLinesAnimationRef.current(winningData);
          } else {
            // Clean up "show all wins" state before skipping to next spin
            if (FEATURES_CONFIG.WIN_LINES.ENABLED && winLinesContainerRef.current) {
              winLinesContainerRef.current.hideWinLine();
            }
            if (globalLineWinTextRef?.current) {
              globalLineWinTextRef.current.hide();
            }
            // Also hide per-line win texts to prevent residual visibility
            const middleReelLineWinText =
              lineWinTextReelRefs?.current?.[GAME_LAYOUT.MIDDLE_REEL];
            const secondReelLineWinText =
              lineWinTextReelRefs?.current?.[GAME_LAYOUT.SECOND_REEL];
            middleReelLineWinText?.hideAllLineWinTexts();
            secondReelLineWinText?.hideAllLineWinTexts();
          }

          if (!shouldShowAllWins || skipShowAllWins) {
            if (isAnimatedWin && currentRound?.type !== "feature") {
              const bigWinDelay =
                FEATURES_CONFIG.WIN_ANIMATION_TIMING
                  .BIG_WIN_DELAY_WITHOUT_SHOW_ALL;

              const bigWinTimeoutId = setTimeout(() => {
                dispatch(closeAllPopups());
                dispatch(setWinAnimationsPlaying(true));
              }, bigWinDelay);

              currentTimeoutsRef.current.push(bigWinTimeoutId);
            }
          }
        })();
      }
    }, FEATURES_CONFIG.WIN_ANIMATION_TIMING.WIN_LINES_DELAY);

    return () => clearTimeout(delayId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    spinComplete,
    currentRound,
    winningLines,
    reelRefs,
    overlayReelRefs,
    isSpinning,
    dispatch,
    bet,
    detectHoldAndWin,
    detectHoldAndWinOutro,
    holdAndWinActive,
  ]);

  /**
   * Executes a callback function on each reel controller.
   */
  const executeOnReelControllers = useCallback(
    (callback) => {
      const reels = reelRefs.current;
      if (!reels) return;

      for (let i = 0; i < reels.length; i++) {
        const controller = reels[i]?.getReelController();
        if (controller) {
          callback(controller, i);
        }
      }
    },
    [reelRefs]
  );

  /**
   * Executes a callback function on each overlay reel controller.
   */
  const executeOnOverlayReelControllers = useCallback(
    (callback) => {
      const overlayReels = overlayReelRefs.current;
      if (!overlayReels) return;

      for (let i = 0; i < overlayReels.length; i++) {
        const controller = overlayReels[i]?.getOverlayReelController();
        if (controller) {
          callback(controller, i);
        }
      }
    },
    [overlayReelRefs]
  );

  useEffect(() => {
    executeOnReelControllersRef.current = executeOnReelControllers;
    executeOnOverlayReelControllersRef.current =
      executeOnOverlayReelControllers;
  }, [executeOnReelControllers, executeOnOverlayReelControllers]);

  /**
   * Resets all visual effects and animations to their default state.
   * Hides win texts, animations, restores symbol textures, and clears dimming overlay.
   * Preserves landing/idle animations when Hold and Win is active.
   */
  const resetAllEffects = useCallback(() => {
    const middleReelLineWinText =
      lineWinTextReelRefs?.current?.[GAME_LAYOUT.MIDDLE_REEL];
    const secondReelLineWinText =
      lineWinTextReelRefs?.current?.[GAME_LAYOUT.SECOND_REEL];
    middleReelLineWinText?.hideAllLineWinTexts();
    secondReelLineWinText?.hideAllLineWinTexts();

    if (globalLineWinTextRef?.current) {
      globalLineWinTextRef.current.hide();
    }

    executeOnOverlayReelControllersRef.current((overlayReelController) => {
      if (overlayReelController) {
        if (!holdAndWinActive) {
          if (overlayReelController.hideAllAnimations) {
            overlayReelController.hideAllAnimations();
          }
        }

        if (
          FEATURES_CONFIG.EXPANDING_WILDS.ENABLED &&
          overlayReelController.isExpandingWildVisible
        ) {
          overlayReelController.forceStopExpandingWildFullReel?.();
        }

        if (overlayReelController.forceDeepSpineCleanup) {
          overlayReelController.forceDeepSpineCleanup();
        }
      }
    });

    executeOnReelControllersRef.current((controller) => {
      if (controller && controller.showAllSymbolTextures) {
        controller.showAllSymbolTextures();
      }
    });

    if (dimReelSetRef.current) {
      dimReelSetRef.current.undimReels();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalLineWinTextRef, holdAndWinActive]);

  /**
   * Matches winning line data with game layout winning lines.
   * Groups multiple wins on the same line together and returns structured data for animation.
   */
  const matchHorizontalWinningLines = useCallback((winningLines, wins) => {
    const result = {};
    if (!Array.isArray(wins)) {
      return result;
    }

    for (let i = 0; i < wins.length; i++) {
      const win = wins[i];
      const { id, positions, symbol, win: winAmount } = win;
      if (!result[id]) {
        result[id] = {
          line: winningLines[id],
          positions: [],
          symbol,
          win: winAmount,
        };
      }
      result[id].positions.push(...positions);
    }

    return result;
  }, []);

  /**
   * Syncs refs with current state values for use in async operations.
   */
  useEffect(() => {
    spinCompleteRef.current = spinComplete;
    currentRoundRef.current = currentRound;
    winningLinesRef.current = winningLines;
    dispatchRef.current = dispatch;
    activityRef.current = isActive;
    turboActiveRef.current = spacebarTurboActive || autoPlayActive;
  }, [spinComplete, currentRound, winningLines, dispatch, isActive, spacebarTurboActive, autoPlayActive]);

  /**
   * Initializes the reelset controller object once on component mount.
   * Creates lazy-loaded getters for controllers and attaches utility methods.
   */
  useEffect(() => {
    if (reelsetControllerRef.current) {
      return;
    }

    reelsetControllerRef.current = {
      _reelControllers: null,
      _overlayReelControllers: null,

      get reelControllers() {
        if (!this._reelControllers) {
          this._reelControllers =
            reelRefs.current
              ?.map((reel) => reel?.getReelController())
              .filter(Boolean) || [];
        }
        return this._reelControllers;
      },

      get overlayReelControllers() {
        if (!this._overlayReelControllers) {
          this._overlayReelControllers =
            overlayReelRefs.current
              ?.map((overlayReel) => overlayReel?.getOverlayReelController())
              .filter(Boolean) || [];
        }
        return this._overlayReelControllers;
      },

      executeOnReelControllers,
      executeOnOverlayReelControllers,
      resetAllEffects,
      matchHorizontalWinningLines,
    };

    isReadyRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Cycles through winning lines one by one, displaying animations and win amounts.
   * Shows win text, restores textures for non-winning symbols, and plays animations for winning symbols.
   * Skips first line cleanup on first cycle to prevent interrupting dimming transition.
   */
  const cycleThroughLines = useCallback(
    async (winningData, lineIds, isFirstEverCycle = false, startIndex = 0) => {
      for (let index = startIndex; index < lineIds.length; index++) {
        // CRITICAL: Check if turbo is active FIRST before doing anything else
        // This prevents per-line text from flashing when user is holding spacebar for turbo
        if (turboActiveRef.current) {
          return;
        }

        if (!isAnimatingRef.current) {
          break;
        }

        const lineId = lineIds[index];
        currentLineIndexRef.current = index;
        const data = winningData[lineId];

        // Skip if data doesn't exist for this line ID (defensive check)
        if (!data) {
          continue;
        }

        // Check if this is a scatter line
        const isScatterLine =
          lineId === "scatter" || data.symbol === SCATTER_SYMBOL_ID;

        // For scatter lines: show global win text
        // For regular lines: show win text on specific symbols
        if (isScatterLine) {
          if (globalLineWinTextRef?.current && data.win !== undefined) {
            globalLineWinTextRef.current.showWinAmount(data.win);
          }
        }

        // Find which reel (2nd or 3rd) has a symbol in this line, prefer middle reel
        let targetReelIndex = null;
        let targetSymbolIndex = null;

        // First check middle reel (index 2)
        for (let i = 0; i < data.positions.length; i++) {
          const [reelIndex, symbolIndex] = data.positions[i];
          if (reelIndex === GAME_LAYOUT.MIDDLE_REEL) {
            targetReelIndex = GAME_LAYOUT.MIDDLE_REEL;
            targetSymbolIndex = symbolIndex;
            break;
          }
        }

        // If middle reel not found, check second reel (index 1)
        if (targetReelIndex === null) {
          for (let i = 0; i < data.positions.length; i++) {
            const [reelIndex, symbolIndex] = data.positions[i];
            if (reelIndex === GAME_LAYOUT.SECOND_REEL) {
              targetReelIndex = GAME_LAYOUT.SECOND_REEL;
              targetSymbolIndex = symbolIndex;
              break;
            }
          }
        }

        // Always hide previous line's win text (including on first line)
        const middleReelLineWinText =
          lineWinTextReelRefs?.current?.[GAME_LAYOUT.MIDDLE_REEL];
        const secondReelLineWinText =
          lineWinTextReelRefs?.current?.[GAME_LAYOUT.SECOND_REEL];
        middleReelLineWinText?.hideAllLineWinTexts();
        secondReelLineWinText?.hideAllLineWinTexts();

        if (globalLineWinTextRef?.current) {
          globalLineWinTextRef.current.hide();
        }

        if (
          !isScatterLine &&
          targetReelIndex !== null &&
          targetSymbolIndex !== null &&
          data.win !== undefined
        ) {
          const targetReelLineWinText =
            lineWinTextReelRefs?.current?.[targetReelIndex];

          if (targetReelLineWinText) {
            targetReelLineWinText.showLineWinText(targetSymbolIndex, data.win);
          }
        }

        // Get positions of current winning line
        const currentLinePositions = data.positions.map(
          ([reelIndex, symbolIndex]) => {
            const reelController =
              reelsetControllerRef.current.reelControllers[reelIndex];
            const symbol =
              reelController?.symbolsContainer.children[symbolIndex];
            const symbolId = symbol?.currentSymbolId || symbol?.symbolId;
            return [reelIndex, symbolIndex, symbolId];
          }
        );

        // Always hide animations not in current line (including first line)
        reelsetControllerRef.current.executeOnOverlayReelControllers(
          (overlayReelController) => {
            overlayReelController?.hideAllAnimationsExcept(
              currentLinePositions
            );
          }
        );

        if (
          FEATURES_CONFIG.EXPANDING_WILDS.ENABLED &&
          FEATURES_CONFIG.EXPANDING_WILDS.ANIMATION_MODE === "full_reel" &&
          overlayReelRefs.current
        ) {
          const reelsInCurrentLine = new Set(
            currentLinePositions.map(([reelIndex]) => reelIndex)
          );

          overlayReelRefs.current.forEach((overlayReelRef) => {
            const overlayReelController =
              overlayReelRef?.getOverlayReelController?.();
            if (
              overlayReelController?.isExpandingWildVisible &&
              !reelsInCurrentLine.has(overlayReelController.reelIndex)
            ) {
              overlayReelRef?.hideExpandingWildFullReel?.();
            }
          });
        }

        if (FEATURES_CONFIG.WIN_LINES.ENABLED && winLinesContainerRef.current) {
          winLinesContainerRef.current.hideWinLine();
        }

        const positionsToRestore = [];
        const reelIndices = GAME_LAYOUT.REEL_INDICES;

        for (let reelIndex = 0; reelIndex < reelIndices.length; reelIndex++) {
          const reel = reelIndices[reelIndex];
          const reelController =
            reelsetControllerRef.current.reelControllers[reel];
          const overlayReelController =
            reelsetControllerRef.current.overlayReelControllers[reel];

          if (!reelController || !overlayReelController) continue;

          for (
            let symbolIndex = 0;
            symbolIndex < GAME_LAYOUT.SYMBOLS_VISIBLE;
            symbolIndex++
          ) {
            let isInCurrentLine = false;
            for (let j = 0; j < currentLinePositions.length; j++) {
              const [lineReelIndex, lineSymbolIndex] = currentLinePositions[j];
              if (lineReelIndex === reel && lineSymbolIndex === symbolIndex) {
                isInCurrentLine = true;
                break;
              }
            }

            if (!isInCurrentLine) {
              positionsToRestore.push([reel, symbolIndex]);
              if (reelController.showSymbolTexture) {
                reelController.showSymbolTexture(symbolIndex);
              }
            }
          }
        }

        if (!activityRef.current) {
          return;
        }

        const positions = data.positions;
        const reelPositions = {};
        for (let i = 0; i < positions.length; i++) {
          const [reelIndex, symbolIndex] = positions[i];
          if (!reelPositions[reelIndex]) {
            reelPositions[reelIndex] = [];
          }
          reelPositions[reelIndex].push([symbolIndex, i]);
        }

        for (let i = 0; i < positions.length; i++) {
          const [reelIndex, symbolIndex] = positions[i];
          const overlayReelController =
            reelsetControllerRef.current.overlayReelControllers[reelIndex];
          const reelController =
            reelsetControllerRef.current.reelControllers[reelIndex];

          if (!overlayReelController || !reelController) continue;

          const hasExpandingWildFullReel =
            FEATURES_CONFIG.EXPANDING_WILDS.ENABLED &&
            FEATURES_CONFIG.EXPANDING_WILDS.ANIMATION_MODE === "full_reel" &&
            overlayReelController.isExpandingWildVisible === true;

          if (hasExpandingWildFullReel) {
            continue;
          }

          const symbol = reelController.symbolsContainer.children[symbolIndex];
          if (symbol) {
            const actualSymbolId = symbol.currentSymbolId || symbol.symbolId;
            if (actualSymbolId !== undefined) {
              overlayReelController.showWinAnimation(
                symbolIndex,
                actualSymbolId
              );
            }
          }
        }

        const rafId = requestAnimationFrame(() => {
          const index = pendingAnimationFramesRef.current.indexOf(rafId);
          if (index > -1) {
            pendingAnimationFramesRef.current.splice(index, 1);
          }

          for (let i = 0; i < positions.length; i++) {
            const [reelIndex, symbolIndex] = positions[i];
            const reelController =
              reelsetControllerRef.current.reelControllers[reelIndex];
            const overlayReelController =
              reelsetControllerRef.current.overlayReelControllers[reelIndex];
            const hasExpandingWildFullReel =
              overlayReelController &&
              FEATURES_CONFIG.EXPANDING_WILDS.ENABLED &&
              FEATURES_CONFIG.EXPANDING_WILDS.ANIMATION_MODE === "full_reel" &&
              overlayReelController.isExpandingWildVisible === true;

            if (hasExpandingWildFullReel) {
              continue;
            }

            if (reelController && reelController.hideSymbolTexture) {
              reelController.hideSymbolTexture(symbolIndex);
            }
          }
        });
        pendingAnimationFramesRef.current.push(rafId);

        if (
          FEATURES_CONFIG.WIN_LINES.ENABLED &&
          winLinesContainerRef.current &&
          lineId !== "scatter"
        ) {
          winLinesContainerRef.current.showWinLine(lineId);
        }

        await new Promise((resolve) => {
          const timeoutId = setTimeout(() => {
            const index = currentTimeoutsRef.current.indexOf(timeoutId);
            if (index > -1) {
              currentTimeoutsRef.current.splice(index, 1);
            }
            resolve();
          }, FEATURES_CONFIG.WIN_ANIMATION_TIMING.LINE_CYCLE_DURATION);
          currentTimeoutsRef.current.push(timeoutId);
        });
        if (!activityRef.current) {
          return;
        }
      }
    },
    [
      winLinesContainerRef,
      globalLineWinTextRef,
      overlayReelRefs,
      lineWinTextReelRefs,
    ]
  );

  /**
   * Shows all winning symbols simultaneously before line cycling begins.
   * Creates a celebration effect by displaying all winning animations at once.
   * For expanding wild reels in cascade mode, restores wild textures before hiding winning positions.
   * Transitions seamlessly to first line by preserving its animations and textures.
   */
  const showAllWinningSymbolsOnce = useCallback(
    async (winningData, hasExpandingWilds, expandingWildReelIndices = []) => {
      // Get all unique positions from all winning lines
      const allPositions = [];
      const lineIds = Object.keys(winningData);

      lineIds.forEach((lineId) => {
        const data = winningData[lineId];
        data.positions.forEach((position) => {
          // Check if position already exists to avoid duplicates
          const [reelIndex, symbolIndex] = position;
          const exists = allPositions.some(
            ([r, s]) => r === reelIndex && s === symbolIndex
          );
          if (!exists) {
            allPositions.push(position);
          }
        });
      });

      executeOnOverlayReelControllersRef.current((overlayReelController) => {
        overlayReelController?.hideAllAnimations();
      });

      executeOnReelControllersRef.current((controller) => {
        if (controller && controller.showAllSymbolTextures) {
          controller.showAllSymbolTextures();
        }
      });

      if (globalLineWinTextRef?.current && currentRoundRef.current?.win) {
        globalLineWinTextRef.current.showWinAmount(currentRoundRef.current.win);
      }

      if (dimReelSetRef.current) {
        dimReelSetRef.current.dimReels();
      }

      for (let i = 0; i < allPositions.length; i++) {
        const [reelIndex, symbolIndex] = allPositions[i];
        const overlayReelController =
          reelsetControllerRef.current.overlayReelControllers[reelIndex];
        const reelController =
          reelsetControllerRef.current.reelControllers[reelIndex];

        if (!overlayReelController || !reelController) continue;

        const hasExpandingWildFullReel =
          FEATURES_CONFIG.EXPANDING_WILDS.ENABLED &&
          FEATURES_CONFIG.EXPANDING_WILDS.ANIMATION_MODE === "full_reel" &&
          overlayReelController.isExpandingWildVisible === true;

        if (hasExpandingWildFullReel) {
          continue;
        }

        const symbol = reelController.symbolsContainer.children[symbolIndex];
        if (symbol) {
          const actualSymbolId = symbol.currentSymbolId || symbol.symbolId;
          if (actualSymbolId !== undefined) {
            overlayReelController.showWinAnimation(symbolIndex, actualSymbolId);
          }
        }
      }

      const isFullReelMode =
        FEATURES_CONFIG.EXPANDING_WILDS.ANIMATION_MODE === "full_reel";
      if (!isFullReelMode && expandingWildReelIndices.length > 0) {
        expandingWildReelIndices.forEach((reelIndex) => {
          const reelController =
            reelsetControllerRef.current.reelControllers[reelIndex];
          if (reelController && reelController.showAllSymbolTextures) {
            reelController.showAllSymbolTextures();
          }
        });
      }

      // Show all win lines simultaneously during "show all wins" phase
      if (FEATURES_CONFIG.WIN_LINES.ENABLED && winLinesContainerRef.current) {
        const regularLineIds = lineIds.filter((id) => id !== "scatter");
        if (regularLineIds.length > 0) {
          winLinesContainerRef.current.showMultipleWinLines(regularLineIds);
        }
      }

      const rafId = requestAnimationFrame(() => {
        const index = pendingAnimationFramesRef.current.indexOf(rafId);
        if (index > -1) {
          pendingAnimationFramesRef.current.splice(index, 1);
        }

        for (let i = 0; i < allPositions.length; i++) {
          const [reelIndex, symbolIndex] = allPositions[i];
          const reelController =
            reelsetControllerRef.current.reelControllers[reelIndex];
          const overlayReelController =
            reelsetControllerRef.current.overlayReelControllers[reelIndex];
          const hasExpandingWildFullReel =
            overlayReelController &&
            FEATURES_CONFIG.EXPANDING_WILDS.ENABLED &&
            FEATURES_CONFIG.EXPANDING_WILDS.ANIMATION_MODE === "full_reel" &&
            overlayReelController.isExpandingWildVisible === true;

          if (hasExpandingWildFullReel) {
            continue;
          }

          if (reelController && reelController.hideSymbolTexture) {
            reelController.hideSymbolTexture(symbolIndex);
          }
        }
      });
      pendingAnimationFramesRef.current.push(rafId);

      const displayDuration = hasExpandingWilds
        ? FEATURES_CONFIG.EXPANDING_WILDS.ALL_WINS_DISPLAY_DURATION
        : FEATURES_CONFIG.SHOW_ALL_WINS.DISPLAY_DURATION;

      await new Promise((resolve) => {
        const timeoutId = setTimeout(() => {
          const index = currentTimeoutsRef.current.indexOf(timeoutId);
          if (index > -1) {
            currentTimeoutsRef.current.splice(index, 1);
          }
          resolve();
        }, displayDuration);
        currentTimeoutsRef.current.push(timeoutId);
      });

      // Check if turbo is active - if so, skip line cycling
      if (turboActiveRef.current) {
        return false; // Return false to indicate we should skip line cycling
      }

      // Don't do any transition work here - let the caller handle it
      // This function should only show all wins, not prepare for line cycling
      return true; // Return true to indicate we should proceed with line cycling
    },
    [globalLineWinTextRef, dimReelSetRef, winLinesContainerRef]
  );

  /**
   * Shows all winning symbols for Hold and Win trigger.
   * Similar to showAllWinningSymbolsOnce but unskippable and doesn't prepare for line cycling.
   * Used when Hold and Win triggers alongside regular line wins.
   * This animation is UNSKIPPABLE - player must watch the win before Hold and Win starts.
   */
  const showAllWinningSymbolsForHoldAndWinTrigger = useCallback(
    async (winningData) => {
      // Get all unique positions from all winning lines
      const allPositions = [];
      const lineIds = Object.keys(winningData);

      lineIds.forEach((lineId) => {
        const data = winningData[lineId];
        data.positions.forEach((position) => {
          const [reelIndex, symbolIndex] = position;
          const exists = allPositions.some(
            ([r, s]) => r === reelIndex && s === symbolIndex
          );
          if (!exists) {
            allPositions.push(position);
          }
        });
      });

      // Hide all animations first
      executeOnOverlayReelControllersRef.current((overlayReelController) => {
        overlayReelController?.hideAllAnimations();
      });

      // Show all symbol textures
      executeOnReelControllersRef.current((controller) => {
        if (controller && controller.showAllSymbolTextures) {
          controller.showAllSymbolTextures();
        }
      });

      // Show global win text with total win amount
      if (globalLineWinTextRef?.current && currentRoundRef.current?.win) {
        globalLineWinTextRef.current.showWinAmount(currentRoundRef.current.win);
      }

      // Dim reels
      if (dimReelSetRef.current) {
        dimReelSetRef.current.dimReels();
      }

      // Show win animations for all winning positions
      for (let i = 0; i < allPositions.length; i++) {
        const [reelIndex, symbolIndex] = allPositions[i];
        const overlayReelController =
          reelsetControllerRef.current.overlayReelControllers[reelIndex];
        const reelController =
          reelsetControllerRef.current.reelControllers[reelIndex];

        if (!overlayReelController || !reelController) continue;

        const symbol = reelController.symbolsContainer.children[symbolIndex];
        if (symbol) {
          const actualSymbolId = symbol.currentSymbolId || symbol.symbolId;
          if (actualSymbolId !== undefined) {
            overlayReelController.showWinAnimation(symbolIndex, actualSymbolId);
          }
        }
      }

      // Show all win lines simultaneously
      if (FEATURES_CONFIG.WIN_LINES.ENABLED && winLinesContainerRef.current) {
        const regularLineIds = lineIds.filter((id) => id !== "scatter");
        if (regularLineIds.length > 0) {
          winLinesContainerRef.current.showMultipleWinLines(regularLineIds);
        }
      }

      // Hide textures in next frame
      const rafId = requestAnimationFrame(() => {
        const index = pendingAnimationFramesRef.current.indexOf(rafId);
        if (index > -1) {
          pendingAnimationFramesRef.current.splice(index, 1);
        }

        for (let i = 0; i < allPositions.length; i++) {
          const [reelIndex, symbolIndex] = allPositions[i];
          const reelController =
            reelsetControllerRef.current.reelControllers[reelIndex];

          if (reelController && reelController.hideSymbolTexture) {
            reelController.hideSymbolTexture(symbolIndex);
          }
        }
      });
      pendingAnimationFramesRef.current.push(rafId);

      // Wait for display duration (unskippable - no turbo check)
      const displayDuration = FEATURES_CONFIG.SHOW_ALL_WINS.DISPLAY_DURATION;

      await new Promise((resolve) => {
        const timeoutId = setTimeout(() => {
          const index = currentTimeoutsRef.current.indexOf(timeoutId);
          if (index > -1) {
            currentTimeoutsRef.current.splice(index, 1);
          }
          resolve();
        }, displayDuration);
        currentTimeoutsRef.current.push(timeoutId);
      });

      // Clean up - hide global win text and win lines before showing coins
      if (globalLineWinTextRef?.current) {
        globalLineWinTextRef.current.hide();
      }
      if (FEATURES_CONFIG.WIN_LINES.ENABLED && winLinesContainerRef.current) {
        winLinesContainerRef.current.hideWinLine();
      }
    },
    [globalLineWinTextRef, dimReelSetRef, winLinesContainerRef]
  );

  /**
   * Starts the win line animation cycle.
   * Cycles through each winning line, showing animations and win amounts.
   * For feature rounds, reorders lines to show scatter first.
   */
  const startWinningLinesAnimation = useCallback(
    async (winningData) => {
      if (isAnimatingRef.current) {
        return;
      }

      // Check if turbo is active before starting
      // Prevents race condition where turbo state changes between decision point and execution
      if (turboActiveRef.current) {
        return;
      }

      const lineIds = Object.keys(winningData);
      const winType = getWinType(
        currentRoundRef.current?.win,
        bet,
        currentRoundRef.current?.type
      );

      const isAnimatedWin =
        winType &&
        [
          WIN_TYPES.BIG_WIN,
          WIN_TYPES.MEGA_WIN,
          WIN_TYPES.SUPER_MEGA_WIN,
        ].includes(winType);

      if (winType && !winSoundPlayedRef.current) {
        if (isAnimatedWin) {
          // Don't play sound here - WinAnimationController handles animated win sounds
          winSoundPlayedRef.current = true;
        }
      }

      isAnimatingRef.current = true;
      clearAllTimeouts();

      // Hide global win text before showing individual lines
      if (globalLineWinTextRef?.current) {
        globalLineWinTextRef.current.hide();
      }

      // Prepare first line transition
      const firstLineId = lineIds[0];
      const firstLineData = winningData[firstLineId];
      const firstLinePositions = firstLineData.positions.map(
        ([reelIndex, symbolIndex]) => {
          const reelController =
            reelsetControllerRef.current.reelControllers[reelIndex];
          const symbol = reelController?.symbolsContainer.children[symbolIndex];
          const symbolId = symbol?.currentSymbolId || symbol?.symbolId;
          return [reelIndex, symbolIndex, symbolId];
        }
      );

      executeOnOverlayReelControllersRef.current((overlayReelController) => {
        overlayReelController?.hideAllAnimationsExcept(firstLinePositions);
      });

      const reelIndices = GAME_LAYOUT.REEL_INDICES;
      for (let reelIndex = 0; reelIndex < reelIndices.length; reelIndex++) {
        const reel = reelIndices[reelIndex];
        const reelController =
          reelsetControllerRef.current.reelControllers[reel];

        if (!reelController) continue;

        for (
          let symbolIndex = 0;
          symbolIndex < GAME_LAYOUT.SYMBOLS_VISIBLE;
          symbolIndex++
        ) {
          let isInFirstLine = false;
          for (let j = 0; j < firstLinePositions.length; j++) {
            const [lineReelIndex, lineSymbolIndex] = firstLinePositions[j];
            if (lineReelIndex === reel && lineSymbolIndex === symbolIndex) {
              isInFirstLine = true;
              break;
            }
          }

          if (!isInFirstLine) {
            if (reelController.showSymbolTexture) {
              reelController.showSymbolTexture(symbolIndex);
            }
          }
        }
      }

      if (
        FEATURES_CONFIG.EXPANDING_WILDS.ENABLED &&
        FEATURES_CONFIG.EXPANDING_WILDS.ANIMATION_MODE === "full_reel" &&
        expandingWildReelIndicesRef.current.length > 0
      ) {
        expandingWildReelIndicesRef.current.forEach((reelIndex) => {
          const overlayReelRef = overlayReelRefs.current?.[reelIndex];
          overlayReelRef?.playExpandingWildFullReelWin?.();
        });
      }

      if (dimReelSetRef.current) {
        dimReelSetRef.current.dimReels();
      }

      if (currentRoundRef.current?.type === "feature") {
        dispatchRef.current(closeAllPopups());
        lineIds.splice(lineIds.indexOf("scatter"), 1);
        lineIds.unshift("scatter");
        lineIds.splice(1);
      }

      winningDataRef.current = winningData;
      lineIdsRef.current = lineIds.slice();
      currentLineIndexRef.current = 0;

      const runSeamlessAnimationCycle = async () => {
        while (isAnimatingRef.current) {
          if (turboActiveRef.current) {
            break;
          }

          try {
            await cycleThroughLines(
              winningData,
              lineIds,
              isFirstAnimationRef.current,
              0
            );

            isFirstAnimationRef.current = false;
            currentLineIndexRef.current = 0;
          } catch (error) {
            break;
          }
        }
      };

      runSeamlessAnimationCycle();
    },
    [
      clearAllTimeouts,
      cycleThroughLines,
      bet,
      dimReelSetRef,
      overlayReelRefs,
      globalLineWinTextRef,
    ]
  );

  const cycleThroughLinesRef = useRef(null);
  cycleThroughLinesRef.current = cycleThroughLines;

  const showAllWinningSymbolsOnceRef = useRef(null);
  showAllWinningSymbolsOnceRef.current = showAllWinningSymbolsOnce;

  const showAllWinningSymbolsForHoldAndWinTriggerRef = useRef(null);
  showAllWinningSymbolsForHoldAndWinTriggerRef.current = showAllWinningSymbolsForHoldAndWinTrigger;

  const startWinningLinesAnimationRef = useRef(null);
  startWinningLinesAnimationRef.current = startWinningLinesAnimation;

  /**
   * Resumes win line animations when app becomes active after being inactive.
   * Continues cycling from where it left off or reconstructs state from current round data.
   */
  useEffect(() => {
    if (!isActive) {
      clearAllTimeouts();
      return;
    }

    const hasWins = !!(
      currentRoundRef.current?.wins &&
      Object.keys(currentRoundRef.current.wins).length > 0
    );

    if (spinCompleteRef.current && hasWins && !animationCompletedRef.current) {
      let resumeLineIds = lineIdsRef.current;
      if (
        !winningDataRef.current ||
        !resumeLineIds ||
        resumeLineIds.length === 0
      ) {
        const wl = winningLinesRef.current;
        const cr = currentRoundRef.current;
        if (!wl || !cr?.wins) return;
        resumeLineIds = Object.keys(cr.wins);
        if (cr.type === "feature") {
          resumeLineIds.splice(resumeLineIds.indexOf("scatter"), 1);
          resumeLineIds.unshift("scatter");
          resumeLineIds.splice(1);
        }
      }

      const startIndex = Math.min(
        Math.max(currentLineIndexRef.current, 0),
        Math.max((resumeLineIds?.length || 1) - 1, 0)
      );

      if (!isAnimatingRef.current) {
        isAnimatingRef.current = true;
      }

      (async () => {
        await cycleThroughLinesRef.current(
          winningDataRef.current || {},
          resumeLineIds || [],
          false,
          startIndex
        );

        if (isAnimatingRef.current) {
          animationCompletedRef.current = true;
          isAnimatingRef.current = false;
        }
      })();
    }
  }, [isActive, clearAllTimeouts]);

  /**
   * Resets all animations and effects when a new spin starts.
   * Only runs for regular game spins, not Hold and Win spins.
   */
  useEffect(() => {
    if (holdAndWinActive) {
      return;
    }

    if (!isSpinning || !reelsetControllerRef.current) return;

    const wasAnimating = isAnimatingRef.current;

    soundManager.stop(SOUND_NAME.expanding_wild);
    soundManager.stop(SOUND_NAME.win_line);

    dispatch(setWinAnimationsPlaying(false));
    dispatch(setCriticalAnimationPlaying(false));
    dispatch(setCounterAnimationStarted(false));
    dispatch(setCounterAnimationFinished(false));
    dispatch(setExpandingWildsOccurred(false));
    dispatch(setSkipWinAnimation(false));
    dispatch(setUserWantsToDismissWinAnimation(false));
    dispatch(setSkipCounterAnimation(false));
    dispatch(setIsWaitingForUserConfirmation(false));

    reelsetControllerRef.current.resetAllEffects();

    if (FEATURES_CONFIG.WIN_LINES.ENABLED && winLinesContainerRef.current) {
      winLinesContainerRef.current.hideWinLine();
    }

    // Explicitly hide all win texts to prevent residual visibility
    const middleReelLineWinText =
      lineWinTextReelRefs?.current?.[GAME_LAYOUT.MIDDLE_REEL];
    const secondReelLineWinText =
      lineWinTextReelRefs?.current?.[GAME_LAYOUT.SECOND_REEL];
    middleReelLineWinText?.hideAllLineWinTexts();
    secondReelLineWinText?.hideAllLineWinTexts();

    if (globalLineWinTextRef?.current) {
      globalLineWinTextRef.current.hide();
    }

    if (wasAnimating) {
      isAnimatingRef.current = false;
      clearAllTimeouts();
    }

    animationCompletedRef.current = false;
    isFirstAnimationRef.current = true;
    animatedRoundsRef.current.clear();
    winSoundPlayedRef.current = false;
    expandingWildReelIndicesRef.current = [];
    winningDataRef.current = null;
    lineIdsRef.current = [];
    currentLineIndexRef.current = 0;
    holdAndWinOutroTriggeredRef.current = false;
  }, [
    isSpinning,
    holdAndWinActive,
    clearAllTimeouts,
    dispatch,
    winLinesContainerRef,
    overlayReelRefs,
    globalLineWinTextRef,
    lineWinTextReelRefs,
  ]);

  /**
   * Resets visual effects when returning from free spins feature.
   */
  useEffect(() => {
    if (resetMainGameVisualEffects) {
      if (reelsetControllerRef.current?.reelControllers) {
        reelsetControllerRef.current.resetAllEffects();

        if (FEATURES_CONFIG.WIN_LINES.ENABLED && winLinesContainerRef.current) {
          winLinesContainerRef.current.hideWinLine();
        }
      }

      if (isAnimatingRef.current) {
        isAnimatingRef.current = false;
        clearAllTimeouts();
      }

      animationCompletedRef.current = false;
      isFirstAnimationRef.current = true;
      animatedRoundsRef.current.clear();

      dispatch(setResetMainGameVisualEffects(false));
    }
  }, [
    resetMainGameVisualEffects,
    clearAllTimeouts,
    dispatch,
    winLinesContainerRef,
  ]);

  /**
   * Shows turbo suggestion popup on non-winning rounds when appropriate.
   */
  useEffect(() => {
    if (!FEATURES_CONFIG.TURBO_SUGGESTION.ENABLED) {
      return;
    }

    if (
      turboSuggestionPending &&
      spinComplete &&
      !isSpinning &&
      !freeSpinsActive
    ) {
      const hasWin = currentRound?.win && currentRound.win > 0;

      if (!hasWin) {
        dispatch(openTurboSuggestionPopup());
        dispatch(setTurboSuggestionPending(false));
      }
    }
  }, [
    turboSuggestionPending,
    spinComplete,
    isSpinning,
    freeSpinsActive,
    currentRound,
    dispatch,
  ]);

  /**
   * Cleanup effect: Clears all timeouts and resets effects when component unmounts.
   */
  useEffect(() => {
    return () => {
      clearAllTimeouts();
      isAnimatingRef.current = false;

      if (reelsetControllerRef.current) {
        reelsetControllerRef.current.resetAllEffects();
      }
    };
  }, [clearAllTimeouts]);

  return {
    reelsetController: reelsetControllerRef.current,
  };
};

export default useWinController;
